import type { AdminUser, CreateAdminUserInput, UpdateAdminUserInput } from '../types/admin';
import { apiRequest } from './apiClient';

/**
 * Signing in, and managing who else can.
 *
 * Separate from `adminApi` because these are the only calls allowed to opt out
 * of the global 401 handling, and that difference should be visible in the
 * import rather than buried in one method of a large object. Everything else
 * rides on the session cookie the browser holds, so no call here takes a
 * credential argument.
 */
export const adminAuthApi = {
  /**
   * A wrong password answers 401. That is the answer, not an expired session,
   * so it must not sign the operator out of a session they may still hold.
   */
  login(email: string, password: string): Promise<{ user: AdminUser; expiresAt: string }> {
    return apiRequest('/admin/auth/login', {
      method: 'POST',
      body: JSON.stringify({ email, password }),
      skipUnauthorizedHandler: true,
    });
  },

  logout(): Promise<void> {
    return apiRequest('/admin/auth/logout', { method: 'POST' });
  },

  /**
   * The app's boot probe. Answers `{ user: null }` when nobody is signed in,
   * so "not signed in" is a normal answer rather than an error to handle.
   */
  me(): Promise<{ user: AdminUser | null }> {
    return apiRequest('/admin/auth/me', { skipUnauthorizedHandler: true });
  },

  changePassword(currentPassword: string, newPassword: string): Promise<void> {
    return apiRequest('/admin/auth/password', {
      method: 'POST',
      body: JSON.stringify({ currentPassword, newPassword }),
      skipUnauthorizedHandler: true,
    });
  },

  listUsers(): Promise<AdminUser[]> {
    return apiRequest('/admin/users');
  },

  createUser(
    input: CreateAdminUserInput,
  ): Promise<AdminUser & { temporaryPassword: string }> {
    return apiRequest('/admin/users', {
      method: 'POST',
      body: JSON.stringify(input),
    });
  },

  updateUser(id: string, changes: UpdateAdminUserInput): Promise<AdminUser> {
    return apiRequest(`/admin/users/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify(changes),
    });
  },

  resetUserPassword(id: string): Promise<{ temporaryPassword: string }> {
    return apiRequest(`/admin/users/${encodeURIComponent(id)}/password-reset`, {
      method: 'POST',
    });
  },
};
